import { Flex } from 'antd'
import { isNil } from 'lodash-es'
import Image from 'next/image'
import { FunctionComponent } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import arrowIconUrl from '@/assets/images/pptx/card-icon/arrow.png'
import eyeIconUrl from '@/assets/images/pptx/card-icon/eye.png'
import likeIconUrl from '@/assets/images/pptx/card-icon/like.png'
import messageIconUrl from '@/assets/images/pptx/card-icon/message.png'
import playIconUrl from '@/assets/images/pptx/card-icon/play.png'
import emptyImageUrl from '@/assets/images/pptx/empty.png'
import routes from '@/hooks/use-authorization/routes'
import { OurIntlShape } from '@/i18n/typing'
import { convertPlatformShortcode } from '@/utils/convert-platform'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import { designLayoutSize, PptxObjectType } from '@/utils/export-pptx/constants'
import { KolInfoSlide } from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'

interface SlideProps {
  slide: KolInfoSlide
  onSlideReady?: (slide: KolInfoSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  useMount(() => {
    onSlideReady?.(slide)
  })

  const platformInfo = convertPlatformShortcode(slide.payload.kolInfo.platform)

  return (
    <Wrapper data-pptx-object={PptxObjectType.Shape}>
      <PlatformImageWrapper>
        <PlatformImage
          height={54}
          platform={slide.payload.kolInfo.platform}
          width={54}
        />
      </PlatformImageWrapper>
      <TitleBlock>
        <Title data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'general:pptx_title_kol_info' })}
        </Title>
        <TopBrandLine data-pptx-object={PptxObjectType.Shape} />
      </TitleBlock>
      <BottomBrandLine data-pptx-object={PptxObjectType.Shape} />
      <ImageWrapper>
        <Image
          priority
          unoptimized
          alt='kol thumbnail'
          data-link={routes.kolId.stringifyUrl({
            kolId: slide.payload.kolInfo.uuid,
          })}
          data-pptx-object={PptxObjectType.Image}
          height={177}
          src={getThumbnail(slide.payload.kolInfo.uuid)}
          style={{ borderRadius: '50%' }}
          width={177}
        />
      </ImageWrapper>
      <BaseInfoWrapper>
        <KolName data-ellipsis data-pptx-object={PptxObjectType.Text}>
          {slide.payload.kolInfo.name}
        </KolName>
        {!isNil(slide.payload.kolInfo.customizedTags) && (
          <Para>
            <ParaFieldName data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:field_name_property' })}
            </ParaFieldName>

            <ParaFieldValue data-pptx-object={PptxObjectType.Text}>
              {slide.payload.kolInfo.customizedTags.length > 0
                ? slide.payload.kolInfo.customizedTags.join(' · ')
                : formatMessage({ id: 'autoreport:chart_label_empty_data' })}
            </ParaFieldValue>
          </Para>
        )}
        {!isNil(slide.payload.kolInfo.summary) && (
          <Para>
            <ParaFieldName data-pptx-object={PptxObjectType.Text}>
              {formatMessage({ id: 'general:column_title_summary' })}
            </ParaFieldName>
            <ParaFieldValue data-pptx-object={PptxObjectType.Text}>
              {slide.payload.kolInfo.summary
                ? slide.payload.kolInfo.summary
                : formatMessage({ id: 'autoreport:chart_label_empty_data' })}
            </ParaFieldValue>
          </Para>
        )}
      </BaseInfoWrapper>
      <KpiInfoWrapper data-pptx-object={PptxObjectType.Shape}>
        <Section>
          <SectionTitleBlock data-pptx-object={PptxObjectType.Text}>
            {platformInfo.title}{' '}
            {formatMessage({ id: 'general:column_title_data_overview' })}
          </SectionTitleBlock>
          <KpiList>
            {slide.payload.kpi.map((kpi, i) => (
              <Kpi key={i}>
                <KpiTitle data-pptx-object={PptxObjectType.Text}>
                  {kpi.title}
                </KpiTitle>
                <KpiValue>
                  <KpiValuePrimary
                    color={kpi?.primaryColor}
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {kpi.primaryText}
                  </KpiValuePrimary>
                  {(kpi?.secondaryTextData ?? []).map(
                    (secondaryTextData, i) => (
                      <KpiValueSecondary
                        color={secondaryTextData.color}
                        data-pptx-object={PptxObjectType.Text}
                        key={`KpiValueSecondary-${i}`}
                      >
                        {secondaryTextData.title}
                      </KpiValueSecondary>
                    ),
                  )}
                </KpiValue>
              </Kpi>
            ))}
          </KpiList>
        </Section>

        <Section>
          <SectionTitleBlock>
            <span data-pptx-object={PptxObjectType.Text}>
              {formatMessage({
                id: 'general:column_title_recent_advertorial_content',
              })}
            </span>
            <span data-pptx-object={PptxObjectType.Shape} />
          </SectionTitleBlock>
          <PostList>
            {slide.payload.posts && slide.payload.posts.length > 0 ? (
              slide.payload.posts.slice(0, 3).map((post, i) => (
                <Post key={i}>
                  <PostImage>
                    <Image
                      priority
                      unoptimized
                      alt='post thumbnail'
                      data-link={post.link}
                      data-pptx-object={PptxObjectType.Image}
                      height={134}
                      src={post.thumbnail}
                      width={215}
                    />
                  </PostImage>
                  <PostTitle
                    data-ellipsis
                    data-pptx-object={PptxObjectType.Text}
                  >
                    {post.title.replaceAll('\n', ' ')}
                  </PostTitle>
                  <IconList>
                    {post.likeCount && (
                      <IconWrapper>
                        <Image
                          priority
                          unoptimized
                          alt='like'
                          data-pptx-object={PptxObjectType.Image}
                          height={16}
                          src={likeIconUrl}
                          width={16}
                        />
                        <span data-pptx-object={PptxObjectType.Text}>
                          {post.likeCount}
                        </span>
                      </IconWrapper>
                    )}
                    {post.commentCount && (
                      <IconWrapper>
                        <Image
                          priority
                          unoptimized
                          alt='message'
                          data-pptx-object={PptxObjectType.Image}
                          height={16}
                          src={messageIconUrl}
                          width={16}
                        />
                        <span data-pptx-object={PptxObjectType.Text}>
                          {post.commentCount}
                        </span>
                      </IconWrapper>
                    )}
                    {post.viewCount && (
                      <IconWrapper>
                        <Image
                          priority
                          unoptimized
                          alt='eye'
                          data-pptx-object={PptxObjectType.Image}
                          height={16}
                          src={eyeIconUrl}
                          width={16}
                        />
                        <span data-pptx-object={PptxObjectType.Text}>
                          {post.viewCount}
                        </span>
                      </IconWrapper>
                    )}
                    {post.playCount && (
                      <IconWrapper>
                        <Image
                          priority
                          unoptimized
                          alt='play'
                          data-pptx-object={PptxObjectType.Image}
                          height={16}
                          src={playIconUrl}
                          width={16}
                        />
                        <span data-pptx-object={PptxObjectType.Text}>
                          {post.playCount}
                        </span>
                      </IconWrapper>
                    )}
                    {post.shareCount && (
                      <IconWrapper>
                        <Image
                          priority
                          unoptimized
                          alt='arrow'
                          data-pptx-object={PptxObjectType.Image}
                          height={16}
                          src={arrowIconUrl}
                          width={16}
                        />
                        <span data-pptx-object={PptxObjectType.Text}>
                          {post.shareCount}
                        </span>
                      </IconWrapper>
                    )}
                  </IconList>
                  {post.relatedBrandUuidList.length > 0 && (
                    <Flex align='center' gap={8}>
                      <RelatedBrand data-pptx-object={PptxObjectType.Text}>
                        {formatMessage({
                          id: 'kol:collaboration_value_estimation_data_block_mention_brand_sub_header_title',
                        })}
                        {formatMessage({ id: 'general:colon' })}
                      </RelatedBrand>
                      <Flex gap={-2}>
                        {post.relatedBrandUuidList.map((brandUuid) => (
                          <Image
                            priority
                            unoptimized
                            alt='kol thumbnail'
                            data-link={routes.kolId.stringifyUrl({
                              kolId: brandUuid,
                            })}
                            data-pptx-object={PptxObjectType.Image}
                            height={24}
                            key={brandUuid}
                            src={getThumbnail(brandUuid)}
                            style={{ borderRadius: '50%' }}
                            width={24}
                          />
                        ))}
                      </Flex>
                    </Flex>
                  )}
                </Post>
              ))
            ) : (
              <EmptyWrapper>
                <Image
                  priority
                  unoptimized
                  alt='empty'
                  data-pptx-object={PptxObjectType.Image}
                  height={44}
                  src={emptyImageUrl}
                  style={{ borderRadius: '50%' }}
                  width={66}
                />
                <div data-pptx-object={PptxObjectType.Text}>
                  {formatMessage({ id: 'autoreport:chart_label_empty_data' })}
                </div>
              </EmptyWrapper>
            )}
          </PostList>
        </Section>
      </KpiInfoWrapper>
    </Wrapper>
  )
}

const ImageWrapper = styled.div`
  position: absolute;
  left: 242px;
  top: 158px;
`

const KolName = styled.div`
  font-size: 27px;
  font-weight: 700;
  line-height: 37px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

const ParaFieldName = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: ${({ color, theme }): string => color ?? theme.colors.text.primary};
`

const ParaFieldValue = styled.div`
  font-size: 18px;
  line-height: 25px;
  color: ${({ color, theme }): string => color ?? theme.colors.text.secondary};
`

const Para = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BaseInfoWrapper = styled.div`
  position: absolute;
  top: 359px;
  left: 88px;
  width: 486px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const KpiTitle = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: ${({ color, theme }): string => color ?? theme.colors.text.secondary};
`

const KpiValuePrimary = styled.span<{ color?: string }>`
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  color: ${({ color, theme }): string => color ?? theme.colors.text.primary};
`

const KpiValueSecondary = styled.span<{ color?: string }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${({ color, theme }): string => color ?? theme.colors.brand.secondary};
`

const KpiValue = styled.div`
  font-weight: 700;
  line-height: 1.1;
  font-size: 26px;

  ${KpiValuePrimary} {
    margin-right: 5px;
  }
`

const Kpi = styled.div`
  ${KpiTitle} {
    margin-bottom: 5px;
  }
`

const KpiList = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: -22px;

  ${Kpi} {
    flex: 0 0 25%;
    margin-bottom: 24px;
  }
`

const PostImage = styled.div`
  height: 134px;
  width: 215px;

  img {
    border-radius: 5px;
    object-fit: cover;
  }
`
const PostTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`
const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.secondary};
  line-height: 1;
  margin-bottom: 4px;

  img {
    margin-right: 3px;
  }

  span {
    padding: 2px;
  }
`
const IconList = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${IconWrapper} {
    margin-right: 10px;
  }
`

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 4px;
  color: ${({ theme }): string => theme.colors.text.placeholder};
  font-size: 14px;
`

const Post = styled.div`
  font-size: 24px;
  line-height: 1.6;

  ${PostImage} {
    margin-bottom: 5px;
  }
  ${PostTitle} {
    margin-bottom: 8px;
  }
  ${IconList} {
    margin-bottom: 8px;
  }
`

const PostList = styled.div`
  display: flex;
  gap: 8px;

  ${Post} {
    width: 218px;
    flex: 0 0 auto;
  }
`

const SectionTitleBlock = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  margin-bottom: 12px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const Section = styled.div`
  margin-top: 24px;

  &:first-child {
    margin-top: 0;
  }
`

const KpiInfoWrapper = styled.div`
  position: absolute;
  top: 73px;
  right: 64px;
  bottom: 81px;
  width: 780px;
  background: ${({ theme }): string => theme.colors.background.white};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 54px;
`

const BrandLine = styled.div`
  position: absolute;
  z-index: 10;
  background: ${({ theme }): string => theme.colors.brand.primary};
  height: 1px;
`

const TopBrandLine = styled(BrandLine)`
  position: static;
  top: 72px;
  left: 358px;
  right: 64px;
`

const BottomBrandLine = styled(BrandLine)`
  bottom: 80px;
  left: 64px;
  right: 64px;
`

const Title = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 42px;
  font-weight: 700;
  line-height: 70px;
`

const TitleBlock = styled.div`
  position: absolute;
  top: 37px;
  left: 145px;
  right: 64px;
  display: flex;
  gap: 24px;

  ${Title} {
    flex: 0 0 auto;
  }

  ${TopBrandLine} {
    margin-top: 34px;
    flex: 1 1 auto;
  }
`

const RelatedBrand = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const PlatformImageWrapper = styled.div`
  position: absolute;
  top: 44px;
  left: 52px;
`

const Wrapper = styled.div`
  font-family: Arial;
  width: ${designLayoutSize.w}px;
  height: ${designLayoutSize.h}px;
  position: relative;
  background-color: ${({ theme }): string => theme.colors.border.divider};
  color: ${({ theme }): string => theme.colors.text.primary};

  &,
  * {
    box-sizing: border-box;
  }
`

export default SlideComponent
